import {inject, injectable} from 'inversify';

import type {FileApiModel} from '../../../../api/media/file';
import type {Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';

import {getFileName, getFileSrc} from '../../../../utils/entity/file';
import {getEntityTypeById} from '../../../../utils/entity/getEntityType';
import {
  isAudioAttachment,
  isDocumentAttachment,
  isImageAttachment,
  isLinkAttachment,
  isVideoAttachment,
} from '../../../../utils/entity/post/attachmentTypeCheck';
import {FileFactory} from '../File.factory';
import {FileModel} from '../File.model';
import {FileExpandJrpc} from './File.expand.jrpc';

@injectable()
export class FileMapperJrpc
  implements IApiDataMapper<FileMapperJrpc.ApiData, FileModel.Props, FileModel>
{
  constructor(
    @inject(FileExpandJrpc) public readonly expand: FileExpandJrpc,
    @inject(FileFactory) private readonly factory: FileFactory,
  ) {}

  mapDataToModel(apiData: FileMapperJrpc.ApiData): FileModel {
    const props = this.mapDataToProps(apiData);

    return this.factory.create(props);
  }

  mapDataToProps(apiData: FileMapperJrpc.ApiData): FileModel.Props {
    const id = apiData._id;
    const userId = apiData.user_id;
    const usedByIds = this.usedByIds(apiData);
    const isUploaded = this.isUploaded(apiData);
    const fileName = this.fileName(apiData);
    const fileType = this.fileType(apiData);
    const description = this.description(apiData);
    const _sources = this.sources(apiData, fileType);

    return {
      _sources,
      apiData,
      description,
      fileName,
      fileType,
      id,
      isUploaded,
      usedByIds,
      userId,
    };
  }

  private computeThumbSrc(
    props: FileMapperJrpc.ApiData,
    thumb: FileModel.THUMBS,
    original: FileModel.Src,
  ): FileModel.Src {
    const {mimetype, size, url} = getFileSrc(props, thumb as any);

    if (url) {
      return {mime: mimetype, size, url};
    }

    return original;
  }

  private description(
    props: FileMapperJrpc.ApiData,
  ): FileModel.Props['description'] {
    let description = '';

    if (props.type === 'url') {
      const {urlrepr} = getFileSrc(props);

      description = new URL(urlrepr).hostname;
    }

    return description;
  }

  private fileName(props: FileMapperJrpc.ApiData): FileModel.Props['fileName'] {
    return getFileName(props);
  }

  private fileType(props: FileMapperJrpc.ApiData): FileModel.Props['fileType'] {
    switch (true) {
      case isLinkAttachment(props):
        return FileModel.Type.LINK;
      case isDocumentAttachment(props):
        return FileModel.Type.DOCUMENT;
      case isImageAttachment(props):
        return FileModel.Type.IMAGE;
      case isVideoAttachment(props):
        return FileModel.Type.VIDEO;
      case isAudioAttachment(props):
        return FileModel.Type.AUDIO;
      default:
        return FileModel.Type.LINK;
    }
  }

  private getImageSrc(props: FileMapperJrpc.ApiData, thumb?: FileModel.THUMBS) {
    const {mimetype, size, url} = getFileSrc(props, thumb as any) ?? {};

    if (url) {
      return {mime: mimetype, size, url};
    }

    return null;
  }

  private isUploaded(
    props: FileMapperJrpc.ApiData,
  ): FileModel.Props['isUploaded'] {
    const isFileId = getEntityTypeById(props._id) === 'file';

    return Boolean(isFileId || props.isUploaded);
  }

  private originalSrc(props: FileMapperJrpc.ApiData): FileModel.Src {
    const src = getFileSrc(props);

    const {mimetype, size, url} = src;

    return {
      mime: mimetype,
      size,
      url: url ?? '',
    };
  }

  private playableSrc(props: FileMapperJrpc.ApiData): Nullable<FileModel.Src> {
    const src = getFileSrc(props, 'playable');

    if (src) {
      const {mimetype, size, url} = src;

      return {
        mime: mimetype,
        size,
        url,
      };
    }

    return null;
  }

  private previewSrc(
    props: FileMapperJrpc.ApiData,
    fileType: FileModel.Type,
  ): Nullable<FileModel.PreviewSrc> {
    let previewData: Nullable<ReturnType<typeof getFileSrc>> = null;

    switch (fileType) {
      case FileModel.Type.IMAGE:
        previewData = getFileSrc(props, 500);
        break;
      case FileModel.Type.LINK:
        if (props.src.image) {
          previewData = getFileSrc(props, 'image');
        }
        break;
      default:
        break;
    }

    if (!previewData) {
      return null;
    }

    const width = previewData.width || 0;
    const height = previewData.height || 0;

    const {mimetype, size, url} = previewData;

    return {
      imageSize: {
        height,
        width,
      },
      mime: mimetype,
      size,
      url,
    };
  }

  private sources(
    props: FileMapperJrpc.ApiData,
    fileType: FileModel.Type,
  ): FileModel.Props['_sources'] {
    const originalSrc = this.originalSrc(props);

    return {
      [FileModel.THUMBS.IMAGE]: this.getImageSrc(props) ?? originalSrc,
      [FileModel.THUMBS.ORIGINAL]: originalSrc,
      [FileModel.THUMBS.PLAYABLE]: this.playableSrc(props),
      [FileModel.THUMBS.PREVIEW]: this.previewSrc(props, fileType),
      [FileModel.THUMBS.THUMB_250]: this.computeThumbSrc(
        props,
        FileModel.THUMBS.THUMB_250,
        originalSrc,
      ),
      [FileModel.THUMBS.THUMB_500]: this.computeThumbSrc(
        props,
        FileModel.THUMBS.THUMB_500,
        originalSrc,
      ),
      [FileModel.THUMBS.THUMB_1000]: this.computeThumbSrc(
        props,
        FileModel.THUMBS.THUMB_1000,
        originalSrc,
      ),
      [FileModel.THUMBS.TRANSCRIPT]: this.transcriptSrc(props),
    };
  }

  private transcriptSrc(
    props: FileMapperJrpc.ApiData,
  ): Nullable<FileModel.Src> {
    const {mimetype, size, url} = getFileSrc(props, 'plot');

    return url ? {mime: mimetype, size, url} : null;
  }

  private usedByIds(
    props: FileMapperJrpc.ApiData,
  ): FileModel.Props['usedByIds'] {
    return props.used_by_ids ?? [];
  }
}

export namespace FileMapperJrpc {
  export type ApiData = FileApiModel & {isUploaded?: boolean};

  export type ExpandedApiData = FileApiModel & FileExpandJrpc.ExpandedFields;
}
